import { useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import {
  canAccessPremiumFeature,
  canImportBankStatement,
  type PremiumFeature,
  type PremiumGateContext,
} from '@/lib/premium-gates';
import { UpgradeModal } from './UpgradeModal';

interface PremiumGateProps {
  feature: PremiumFeature;
  featureLabel: string;
  children: ReactNode;
  /** For bank import: use import-specific limit */
  useImportLimit?: boolean;
  fallback?: ReactNode;
}

export function PremiumGate({
  feature,
  featureLabel,
  children,
  useImportLimit = false,
  fallback,
}: PremiumGateProps) {
  const { isAdmin } = useAuth();
  const { profile } = useProfile();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const ctx: PremiumGateContext = { isAdmin, profile };
  const allowed = useImportLimit
    ? canImportBankStatement(ctx)
    : canAccessPremiumFeature(feature, ctx);

  if (allowed) return <>{children}</>;

  return (
    <>
      {fallback ?? (
        <div
          className="rounded-lg border border-dashed p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setUpgradeOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && setUpgradeOpen(true)}
          role="button"
          tabIndex={0}
        >
          <p className="font-medium">{featureLabel}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Función Premium — toca para ver planes
          </p>
        </div>
      )}
      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        featureName={featureLabel}
      />
    </>
  );
}

export function usePremiumGate(feature: PremiumFeature, useImportLimit = false) {
  const { isAdmin } = useAuth();
  const { profile } = useProfile();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const ctx: PremiumGateContext = { isAdmin, profile };
  const allowed = useImportLimit
    ? canImportBankStatement(ctx)
    : canAccessPremiumFeature(feature, ctx);

  return { allowed, upgradeOpen, setUpgradeOpen, ctx };
}
